import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { format } from 'date-fns';

const ShareRidePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const rideId = searchParams.get('id');
  const { user } = useAuth();
  const [ride, setRide] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (rideId) {
      fetchRideDetails();
    } else {
      setError('No ride ID provided');
      setLoading(false);
    }
  }, [rideId]);

  const fetchRideDetails = async () => {
    try {
      // Use public API endpoint that doesn't require authentication
      const apiBaseURL = import.meta.env.VITE_API_URL || 'https://ride-connect-backend-git-main-ro-garcias-projects.vercel.app';
      const response = await fetch(`${apiBaseURL}/api/public/rides/${rideId}`);
      
      if (!response.ok) {
        throw new Error('Ride not found');
      }
      
      const data = await response.json();
      setRide(data);
    } catch (err: any) {
      console.error('Error fetching ride:', err);
      setError(err.message || 'Failed to load ride details');
    } finally {
      setLoading(false);
    }
  };

  const getWebPlatformUrl = () => {
    // Use VITE_WEB_URL env var if set, otherwise use current origin, fallback to rider.ride-connect.app
    return import.meta.env.VITE_WEB_URL || window.location.origin || 'https://rider.ride-connect.app';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading ride details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !ride) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error || 'Ride not found or has expired'}</p>
            <Link
              to="/rides"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Browse Rides
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Format date
  const timeStr = ride.departureTime || '00:00';
  const dateStr = String(ride.departureDate).split('T')[0];
  const dateTimeString = `${dateStr}T${timeStr}`;
  const rideDate = new Date(dateTimeString);
  const formattedDate = format(rideDate, 'EEEE, MMMM d, yyyy');
  const formattedTime = format(rideDate, 'h:mm a');

  const webPlatformUrl = getWebPlatformUrl();
  const joinUrl = `${webPlatformUrl}/rides/${rideId}`;
  const loginUrl = `${webPlatformUrl}/login?redirect=/rides/${rideId}`;
  const registerUrl = `${webPlatformUrl}/register?redirect=/rides/${rideId}`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 text-center">
          <h1 className="text-3xl font-bold mb-2">RideConnect</h1>
          <p className="text-blue-100">Join the Ride!</p>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Ride Card */}
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{ride.name}</h2>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-center text-gray-700">
                <span className="mr-2">📅</span>
                <span>{formattedDate} at {formattedTime}</span>
              </div>
              <div className="flex items-center text-gray-700">
                <span className="mr-2">📍</span>
                <span>{ride.departureLocation || ride.storeLocation || ride.store?.location || 'Location TBD'}</span>
              </div>
              <div className="flex items-center text-gray-700">
                <span className="mr-2">📏</span>
                <span><strong>{ride.distanceKm || 0}</strong> km</span>
              </div>
              <div className="flex items-center text-gray-700">
                <span className="mr-2">👥</span>
                <span><strong>{ride.participantCount || 0}</strong> / <strong>{ride.maxRiders || 20}</strong> riders</span>
              </div>
            </div>

            {ride.description && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-gray-600 leading-relaxed">{ride.description}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            {user ? (
              <Link
                to={joinUrl}
                className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl text-center transition"
              >
                Join This Ride
              </Link>
            ) : (
              <>
                <Link
                  to={joinUrl}
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl text-center transition"
                >
                  Join This Ride
                </Link>
                <div className="text-center">
                  <p className="text-gray-600 text-sm mb-3">Already have an account?</p>
                  <div className="flex gap-3">
                    <Link
                      to={loginUrl}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-xl text-center transition"
                    >
                      Login
                    </Link>
                    <Link
                      to={registerUrl}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-xl text-center transition"
                    >
                      Sign Up
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-8 py-4 text-center">
          <p className="text-gray-500 text-sm">RideConnect - Connecting Cyclists, One Ride at a Time</p>
        </div>
      </div>
    </div>
  );
};

export default ShareRidePage;

